import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import {
  AISummaryErrorResponse,
  AISummaryGenerateResponse,
  AISummaryScope,
  GenerateAISummaryPayload,
} from 'src/interfaces/aiSummary.interface'
import { getAISummariesScopeKey } from 'src/reactQuery/useGetAISummariesQuery'
import { applyAISummaryLimit } from 'src/reactQuery/utils/aiSummaryCache'
import { AISummaryApiService } from 'src/services/aiSummaryApi'

interface UseGenerateAISummaryMutationParams {
  onError?: (error: AxiosError<AISummaryErrorResponse>) => void
  onSuccess?: (data: AISummaryGenerateResponse) => void
  scope: AISummaryScope
}

export const useGenerateAISummaryMutation = ({
  onError,
  onSuccess,
  scope,
}: UseGenerateAISummaryMutationParams) => {
  const queryClient = useQueryClient()
  // Prefix match: refreshes every cached range of this scope — the new
  // summary is a tab everywhere, and the daily cap is shared by all ranges.
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getAISummariesScopeKey(scope) })

  return useMutation({
    mutationFn: (payload: GenerateAISummaryPayload) =>
      AISummaryApiService.generateSummary(scope, payload),
    onError: (error: AxiosError<AISummaryErrorResponse>) => {
      toast.error(error.response?.data?.error || 'Failed to generate summary')
      // 409 (this period was summarised < 24h ago, e.g. in another tab) or
      // 429 (daily cap): refetch so the summary / countdown appears instead of
      // a button that keeps failing.
      const status = error.response?.status
      if (status === 409 || status === 429) {
        applyAISummaryLimit(queryClient, getAISummariesScopeKey(scope), {
          nextAvailableAt: error.response?.data?.nextAvailableAt,
        })
        refresh()
      }
      onError?.(error)
    },
    onSuccess: (data) => {
      applyAISummaryLimit(queryClient, getAISummariesScopeKey(scope), {
        nextAvailableAt: data.data?.nextAvailableAt,
        summary: data.data?.summary,
      })
      refresh()
      onSuccess?.(data)
    },
  })
}
